import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material';
import {
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { useToast, ToastType } from '../../context/ToastContext';

const ShareButton: React.FC<{ url?: string }> = ({ url = window.location.href }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const { notify } = useToast();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      notify('Link copied to clipboard! 📋', ToastType.SUCCESS);
      handleClose();
    } catch (err) {
      console.error('Failed to copy text: ', err);
      notify('Failed to copy link to clipboard', ToastType.ERROR);
      handleClose();
    }
  };

  const shareViaEmail = () => {
    window.open(`mailto:?body=${encodeURIComponent(url)}`);
    handleClose();
  };

  const shareViaTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`);
    handleClose();
  };

  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
    handleClose();
  };

  const shareViaWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(url)}`);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ShareIcon sx={{ fontSize: 18 }} />}
        onClick={handleClick}
        aria-controls="share-menu"
        aria-haspopup="true"
        sx={{
          borderRadius: 2,
          px: 2,
          py: 1,
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
          color: theme.palette.common.white,
          background: alpha(theme.palette.common.white, 0.1),
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: alpha(theme.palette.common.white, 0.2),
            borderColor: alpha(theme.palette.common.white, 0.5),
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`,
          }
        }}
      >
        Share
      </Button>

      <Menu
        id="share-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            minWidth: 200,
            background: `linear-gradient(145deg, 
              ${alpha(theme.palette.background.paper, 0.95)} 0%, 
              ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            backdropFilter: "blur(20px) saturate(1.8)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: `
              0 16px 40px ${alpha(theme.palette.common.black, 0.15)},
              0 8px 20px ${alpha(theme.palette.common.black, 0.1)},
              inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}
            `,
            "& .MuiMenuItem-root": {
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                  ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                transform: "translateX(4px)",
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              "& .MuiListItemIcon-root": {
                minWidth: 36,
              },
              "& .MuiListItemText-primary": {
                fontWeight: 500,
                fontSize: "0.95rem",
              },
            },
          },
        }}
      >
        <MenuItem onClick={copyToClipboard}>
          <ListItemIcon>
            <CopyIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          </ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>

        <MenuItem onClick={shareViaEmail}>
          <ListItemIcon>
            <EmailIcon fontSize="small" sx={{ color: '#EA4335' }} />
          </ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>

        <MenuItem onClick={shareViaTwitter}>
          <ListItemIcon>
            <XIcon fontSize="small" sx={{ color: '#000000' }} />
          </ListItemIcon>
          <ListItemText>X (Twitter)</ListItemText>
        </MenuItem>

        <MenuItem onClick={shareViaFacebook}>
          <ListItemIcon>
          <FacebookIcon fontSize="small" sx={{ color: '#1877F2' }} />
          </ListItemIcon>
          <ListItemText>Facebook</ListItemText>
        </MenuItem>

        <MenuItem onClick={shareViaWhatsApp}>
          <ListItemIcon>
          <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ShareButton;